import { Button, Icon } from '@chakra-ui/react';
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle';
import { atom, useAtom, useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { LDFlags } from '../../../../../../constants/flags';
import { useFlag } from '../../../../../../hooks';
import { trackConversationOpened } from '../../../../../../services/analytics/marketplaceEvents';
import { useMarketplaceExternalId } from '../../../../../../services/marketplace/externalId';
import { useGetLeadByExternalId } from '../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import { formatVehicleSummary } from '../../../../../../utils/cars';
import UnreadCountBubble from '../../../../notifications/UnreadCountBubble';
import ConversationThreadDrawer from './ConversationThreadDrawer';
import { PLATFORM_LABEL, getMessagesByOrg, getUnreadCount, isPlatformChatAvailable } from './utils';

const PLATFORM_POLL_INTERVAL_MS = 15_000;

const platformChatOpenAtom = atom(false);

export const useOpenPlatformChat = () => {
  const setIsOpen = useSetAtom(platformChatOpenAtom);
  return (leadId: string) => {
    trackConversationOpened(leadId, { kind: 'platform' });
    setIsOpen(true);
  };
};

type PlatformChatProps = {
  dealId: string;
};

const PlatformChat = ({ dealId }: PlatformChatProps) => {
  const messagingEnabled = useFlag(LDFlags.MARKETPLACE_MESSAGING);
  const externalId = useMarketplaceExternalId(dealId);
  const [isOpen, setIsOpen] = useAtom(platformChatOpenAtom);
  const openChat = useOpenPlatformChat();

  const leadQuery = useGetLeadByExternalId(
    { externalId },
    {
      query: {
        enabled: messagingEnabled && externalId !== '',
        refetchInterval: isOpen ? PLATFORM_POLL_INTERVAL_MS : false,
      },
    },
  );
  const lead = leadQuery.data;
  const thread = getMessagesByOrg(lead?.messages).get(PLATFORM_LABEL);

  useEffect(() => setIsOpen(false), [externalId]);

  if (!messagingEnabled || !isPlatformChatAvailable(lead)) return null;

  const vehicleName = formatVehicleSummary({ year: lead.year, make: lead.make, model: lead.model });
  const listingLabel = vehicleName === '' ? 'listing' : `${vehicleName} listing`;
  const contextNote = `You're messaging about your ${listingLabel}. Ask a Lease End rep anything about selling your leased car, we're here to help.`;

  const unreadCount = getUnreadCount(thread);

  const handleClose = () => setIsOpen(false);

  return (
    <>
      <Button
        aria-label={`Message ${PLATFORM_LABEL}`}
        position="relative"
        variant="link"
        mr={3}
        minW="auto"
        onClick={() => openChat(lead.id)}
      >
        <UnreadCountBubble count={unreadCount} position="absolute" top={-1} right={-2} />
        <Icon color="taupeGray" as={FiMessageCircle} boxSize="26px" />
      </Button>
      <ConversationThreadDrawer
        target={isOpen ? { kind: 'platform', contextNote } : null}
        thread={thread}
        externalId={externalId}
        leadId={lead.id}
        onClose={handleClose}
      />
    </>
  );
};

export default PlatformChat;
